import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { LETTERHEAD_IMG } from "../lib/letterheadImg";

export default function Settings() {
    const [form, setForm] = useState({
        name: "",
        address: "",
        phone: "",
        email: "",
        logo_url: "",
        tagline: "",
        city: "",
        sign_manager_name: "",
        sign_director_name: "",
    });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        api.get("/settings")
            .then((res) => setForm(res.data))
            .finally(() => setLoading(false));
    }, []);

    async function save() {
        setSaving(true);
        try {
            const res = await api.put("/settings", form);
            setForm(res.data);
            toast.success("Pengaturan tersimpan");
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setSaving(false);
        }
    }

    if (loading) return <div className="text-[#4A5E57]">Memuat...</div>;

    function handleLogoUpload(e) {
        const file = e.target.files?.[0];
        e.target.value = "";
        if (!file) return;
        const reader = new FileReader();
        reader.onerror = () => toast.error("Gagal membaca file");
        reader.onload = () => {
            const img = new Image();
            img.onload = () => {
                // Perkecil ke maks 256px agar ringan disimpan sebagai base64
                const max = 256;
                const scale = Math.min(1, max / Math.max(img.width, img.height));
                const canvas = document.createElement("canvas");
                canvas.width = Math.round(img.width * scale);
                canvas.height = Math.round(img.height * scale);
                canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
                const dataUrl = canvas.toDataURL("image/png");
                setForm((f) => ({ ...f, logo_url: dataUrl }));
                toast.success("Logo dimuat — klik Simpan Pengaturan untuk menerapkan");
            };
            img.onerror = () => toast.error("File gambar tidak valid");
            img.src = reader.result;
        };
        reader.readAsDataURL(file);
    }

    return (
        <div className="space-y-6" data-testid="settings-page">
            <div>
                <div className="label-uppercase mb-2">Pengaturan</div>
                <h1 className="font-heading text-4xl tracking-tight font-semibold">
                    Kop Surat Rumah Sakit
                </h1>
                <p className="text-[#4A5E57] mt-2 max-w-2xl">
                    PDF proposal & pengajuan internal menggunakan kop surat resmi RS Mulya
                    (lihat pratinjau). Data di bawah dipakai untuk pesan WhatsApp dan nama
                    penandatangan pengajuan harga paket.
                </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 surface-card p-6 space-y-4">
                    <div>
                        <Label>Nama Rumah Sakit</Label>
                        <Input
                            value={form.name}
                            data-testid="set-name"
                            onChange={(e) => setForm({ ...form, name: e.target.value })}
                        />
                    </div>
                    <div>
                        <Label>Alamat</Label>
                        <Textarea
                            rows={2}
                            value={form.address}
                            data-testid="set-address"
                            onChange={(e) => setForm({ ...form, address: e.target.value })}
                        />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label>Telepon</Label>
                            <Input
                                value={form.phone}
                                data-testid="set-phone"
                                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                            />
                        </div>
                        <div>
                            <Label>Email</Label>
                            <Input
                                value={form.email}
                                data-testid="set-email"
                                onChange={(e) => setForm({ ...form, email: e.target.value })}
                            />
                        </div>
                    </div>
                    <div>
                        <Label>Logo Rumah Sakit</Label>
                        <div className="flex items-center gap-4 mt-1">
                            {form.logo_url ? (
                                <img
                                    src={form.logo_url}
                                    alt="Logo RS"
                                    data-testid="set-logo-preview"
                                    className="h-16 w-16 object-contain rounded-md border border-[#D1DCD8] bg-white p-1"
                                />
                            ) : (
                                <div className="h-16 w-16 rounded-md border border-dashed border-[#D1DCD8] flex items-center justify-center text-xs text-[#6A9C89]">
                                    Logo
                                </div>
                            )}
                            <div className="space-x-2">
                                <Button
                                    type="button"
                                    variant="outline"
                                    data-testid="set-logo-upload-btn"
                                    onClick={() => document.getElementById("logo-file-input").click()}
                                    className="border-[#D1DCD8] text-[#245B4B] hover:bg-[#E9EFEA]"
                                >
                                    Upload Logo
                                </Button>
                                {form.logo_url && (
                                    <Button
                                        type="button"
                                        variant="outline"
                                        data-testid="set-logo-remove-btn"
                                        onClick={() => setForm({ ...form, logo_url: "" })}
                                        className="border-[#D1DCD8] text-[#B3432B] hover:bg-[#F5E6E1]"
                                    >
                                        Hapus
                                    </Button>
                                )}
                            </div>
                            <input
                                id="logo-file-input"
                                type="file"
                                accept="image/png,image/jpeg,image/webp"
                                className="hidden"
                                data-testid="set-logo-input"
                                onChange={handleLogoUpload}
                            />
                        </div>
                        <div className="text-xs text-[#6A9C89] mt-2">
                            PNG/JPG, otomatis diperkecil. Tampil di header dashboard &amp; halaman
                            login setelah disimpan.
                        </div>
                    </div>
                    <div>
                        <Label>Tagline</Label>
                        <Input
                            value={form.tagline}
                            data-testid="set-tagline"
                            onChange={(e) => setForm({ ...form, tagline: e.target.value })}
                        />
                    </div>
                    <div className="pt-4 border-t border-[#D1DCD8]">
                        <div className="label-uppercase mb-3">
                            Penandatangan Pengajuan Internal
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                            <div>
                                <Label>Kota (untuk tanggal surat)</Label>
                                <Input
                                    value={form.city || ""}
                                    data-testid="set-city"
                                    onChange={(e) => setForm({ ...form, city: e.target.value })}
                                    placeholder="Tangerang"
                                />
                            </div>
                            <div>
                                <Label>Nama Manager Keuangan</Label>
                                <Input
                                    value={form.sign_manager_name || ""}
                                    data-testid="set-sign-manager"
                                    onChange={(e) =>
                                        setForm({ ...form, sign_manager_name: e.target.value })
                                    }
                                    placeholder="Nama lengkap & gelar"
                                />
                            </div>
                            <div>
                                <Label>Nama Direktur</Label>
                                <Input
                                    value={form.sign_director_name || ""}
                                    data-testid="set-sign-director"
                                    onChange={(e) =>
                                        setForm({ ...form, sign_director_name: e.target.value })
                                    }
                                    placeholder="Nama lengkap & gelar"
                                />
                            </div>
                        </div>
                        <div className="text-xs text-[#6A9C89] mt-2">
                            Nama ini muncul di area tanda tangan PDF "Pengajuan Internal" harga paket.
                        </div>
                    </div>
                    <Button
                        onClick={save}
                        disabled={saving}
                        data-testid="set-save-btn"
                        className="bg-[#245B4B] hover:bg-[#1A4236] text-white"
                    >
                        {saving ? "Menyimpan..." : "Simpan Pengaturan"}
                    </Button>
                </div>

                <div className="lg:col-span-1">
                    <div className="surface-card overflow-hidden sticky top-6">
                        <img
                            src={LETTERHEAD_IMG}
                            alt="Kop Surat RS Mulya"
                            data-testid="set-letterhead-preview"
                            className="w-full border-b border-[#D1DCD8]"
                            style={{ maxHeight: 340, objectFit: "cover", objectPosition: "top" }}
                        />
                        <div className="p-5 text-sm text-[#4A5E57] space-y-1">
                            <div className="font-medium text-[#1A362D]">
                                {form.name || "Nama Rumah Sakit"}
                            </div>
                            <div>{form.address || "Alamat rumah sakit"}</div>
                            <div>Telp: {form.phone || "-"}</div>
                            <div>Email: {form.email || "-"}</div>
                        </div>
                        <div className="p-5 border-t border-[#D1DCD8]">
                            <div className="text-xs uppercase tracking-widest text-[#6A9C89]">
                                Pratinjau Kop Surat Resmi (dipakai di semua PDF)
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
